import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type ImgHTMLAttributes,
  type ReactNode,
} from 'react'
import { cn } from '@/lib/utils'

type ImageStatus = 'loading' | 'loaded' | 'error'

/** URLs that have finished loading — avoids shimmer when components remount (e.g. banner carousel). */
const loadedImageUrls = new Set<string>()

function initialImageStatus(src?: string | null): ImageStatus {
  if (!src) return 'error'
  if (loadedImageUrls.has(src)) return 'loaded'
  return 'loading'
}

interface StoreImageProps
  extends Omit<
    ImgHTMLAttributes<HTMLImageElement>,
    'src' | 'loading' | 'fetchPriority' | 'decoding'
  > {
  src?: string | null
  alt: string
  containerClassName?: string
  objectFit?: 'cover' | 'contain'
  /** `box` fits inside a fixed parent; `responsive` scales to full container width. */
  sizing?: 'box' | 'responsive'
  spinnerSize?: 'sm' | 'md' | 'lg'
  fallback?: ReactNode
  /** Above-the-fold / LCP images — eager load with high fetch priority. */
  priority?: boolean
}

function resolveImageStatus(img: HTMLImageElement): ImageStatus {
  if (!img.complete) return 'loading'
  return img.naturalWidth > 0 ? 'loaded' : 'error'
}

function showsSkeletonShimmer(
  status: ImageStatus,
  objectFit: 'cover' | 'contain',
  sizing: 'box' | 'responsive'
): boolean {
  return (
    status === 'loading' &&
    (objectFit === 'cover' ||
      (objectFit === 'contain' && sizing === 'responsive') ||
      (objectFit === 'contain' && sizing === 'box'))
  )
}

export function StoreImage({
  src,
  alt,
  className,
  containerClassName,
  objectFit = 'cover',
  sizing = 'box',
  spinnerSize: _spinnerSize = 'md',
  fallback,
  priority = false,
  ...props
}: StoreImageProps) {
  const imgRef = useRef<HTMLImageElement>(null)
  const [status, setStatus] = useState<ImageStatus>(() => initialImageStatus(src))

  const markLoaded = useCallback((url: string) => {
    loadedImageUrls.add(url)
    setStatus('loaded')
  }, [])

  const handleLoad = useCallback(() => {
    if (src) markLoaded(src)
  }, [markLoaded, src])

  const handleError = useCallback(() => {
    setStatus('error')
  }, [])

  useLayoutEffect(() => {
    if (!src) {
      setStatus('error')
      return
    }

    if (loadedImageUrls.has(src)) {
      setStatus('loaded')
      return
    }

    const img = imgRef.current
    if (!img) return

    const sync = () => {
      const next = resolveImageStatus(img)
      if (next === 'loaded' && src) {
        loadedImageUrls.add(src)
      }
      setStatus(next)
    }

    sync()

    if (img.complete) return

    img.addEventListener('load', sync)
    img.addEventListener('error', sync)

    return () => {
      img.removeEventListener('load', sync)
      img.removeEventListener('error', sync)
    }
  }, [src])

  const showPlaceholder = status === 'loading' || status === 'error'
  const shimmer = showsSkeletonShimmer(status, objectFit, sizing)

  return (
    <div
      className={cn(
        'relative overflow-hidden',
        objectFit === 'cover' && 'h-full w-full bg-luxury-800',
        objectFit === 'contain' && sizing === 'responsive' && 'w-full bg-luxury-800',
        objectFit === 'contain' &&
          sizing === 'responsive' &&
          status === 'loading' &&
          'min-h-72',
        objectFit === 'contain' && sizing === 'box' && 'bg-luxury-800',
        containerClassName
      )}
    >
      {showPlaceholder && (
        <div
          className={cn(
            'absolute inset-0 flex items-center justify-center',
            shimmer && 'skeleton-shimmer'
          )}
          aria-hidden
        >
          {status === 'error' &&
            (fallback ?? (
              <span className="font-display text-zinc-600 select-none">OK</span>
            ))}
        </div>
      )}
      {src && (
        <img
          key={src}
          ref={imgRef}
          src={src}
          alt={alt}
          loading={priority ? 'eager' : 'lazy'}
          fetchPriority={priority ? 'high' : 'auto'}
          decoding="async"
          className={cn(
            objectFit === 'cover'
              ? 'h-full w-full object-cover'
              : sizing === 'responsive'
                ? 'block h-auto w-full object-contain'
                : 'max-h-full max-w-full object-contain',
            'transition-opacity duration-300',
            status === 'loaded' ? 'opacity-100' : 'opacity-0',
            status === 'error' && 'hidden',
            className
          )}
          onLoad={handleLoad}
          onError={handleError}
          {...props}
        />
      )}
    </div>
  )
}
